import { MaskLines, Reveal } from "../components/anim";
import { FileDown } from "lucide-react";
import ParallaxImage from "../components/ParallaxImage";
import { Eyebrow, CTAButton } from "../components/ui-bits";
import { ALLESTIMENTI_VARIANTS, IMG, SUPPLIER } from "../data/site";
import useSEO from "../hooks/useSEO";

const CARDS = [
  {
    n: "01",
    title: "Banco di mescita",
    img: IMG.banco,
    alt: "Il banco originale Sprizziamoci",
    lines: [
      "Struttura nera con dettagli arancioni e piano superiore in acciaio inox.",
      "Spazio sotto il piano per i fusti e gli elementi tecnici dell'impianto.",
    ],
  },
  {
    n: "02",
    title: "Banco di preparazione e servizio",
    img: IMG.limeIce,
    alt: "Preparazione con ghiaccio e agrumi freschi",
    lines: [
      "Vasca per il ghiaccio e vaschetta incassata per le arance tagliate.",
      "Cannucce, accessori e piano di appoggio per i bicchieri.",
    ],
  },
  {
    n: "03",
    title: "Attrezzature e materiali",
    img: IMG.glassesIce,
    alt: "Bicchieri, ghiaccio e materiali di servizio",
    lines: [
      "Impianto di spillatura, erogatori e fusti delle bevande selezionate.",
      "Ghiaccio, bicchieri, guarnizioni e taglio degli agrumi.",
      "Tavolo di supporto e personale secondo il pacchetto concordato.",
    ],
  },
];

export default function Allestimenti() {
  useSEO(
    "Allestimenti: banco di mescita, preparazione e attrezzature | Sprizziamoci",
    "Cosa arriva sul posto: banco di mescita con erogatori, banco di preparazione con vasca ghiaccio e vaschetta arance, attrezzature complete e allestimento wedding effetto legno."
  );
  return (
    <article data-testid="allestimenti-page">
      <section className="pt-32 md:pt-44 pb-14">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Eyebrow>Il nostro allestimento</Eyebrow>
          <MaskLines
            lines={["ARRIVIAMO NOI.", "PREPARIAMO TUTTO.", "SI BRINDA."]}
            as="h1"
            when="load"
            delay={0.15}
            lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.6rem,6.5vw,5.5rem)]"
            className="mt-6"
          />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-white/65">
              Ecco cosa arriva sul posto. Dotazioni e quantità: definite nel preventivo.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8 space-y-6">
          <Reveal>
            <div
              data-testid="banco-originale-card"
              className="grid md:grid-cols-12 gap-0 overflow-hidden rounded-3xl border border-brand-orange/30 bg-brand-surface/30 group"
            >
              <div className="md:col-span-5 overflow-hidden">
                <img
                  src={IMG.banco}
                  alt="Il banco originale Sprizziamoci: struttura nera con dettagli arancioni, erogatori per spritz, campari, gin tonic e gin lemon e modula analcolici Pasquini"
                  loading="lazy"
                  data-testid="banco-originale-image"
                  className="h-80 md:h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </div>
              <div className="md:col-span-7 p-8 md:p-12">
                <span className="font-display italic text-brand-orange text-3xl">01</span>
                <h3 className="mt-2 font-display text-2xl md:text-4xl font-semibold">
                  Il banco di mescita originale
                </h3>
                <ul className="mt-6 space-y-3">
                  {CARDS[0].lines.map((l, j) => (
                    <li key={j} className="flex gap-3 text-sm md:text-base leading-relaxed text-white/60">
                      <span className="mt-2 block h-1.5 w-1.5 rotate-45 bg-brand-orange shrink-0" />
                      {l}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-wrap gap-2.5">
                  {["Larghezza 100 cm", "Altezza 112 cm", "Profondità 80 cm", "Piano in acciaio inox", "4 erogatori"].map((s) => (
                    <span key={s} className="rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-xs text-white/70">
                      {s}
                    </span>
                  ))}
                </div>
                <p className="mt-7 text-xs text-white/40 italic">
                  Il banco originale Sprizziamoci: quattro erogatori per spritz, campari, gin tonic
                  e gin lemon, più la modula analcolici Pasquini.
                </p>
              </div>
            </div>
          </Reveal>
          {CARDS.slice(1).map((c, i) => (
            <Reveal key={c.n} delay={i * 0.06}>
              <div className="grid md:grid-cols-12 gap-0 overflow-hidden rounded-3xl border border-white/[0.07] bg-brand-surface/30 group">
                <div className="md:col-span-4 overflow-hidden min-h-[240px]">
                  <img
                    src={c.img}
                    alt={c.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="md:col-span-8 p-8 md:p-12">
                  <span className="font-display italic text-brand-orange text-3xl">{c.n}</span>
                  <h3 className="mt-2 font-display text-2xl md:text-4xl font-semibold">{c.title}</h3>
                  <ul className="mt-6 space-y-3">
                    {c.lines.map((l, j) => (
                      <li key={j} className="flex gap-3 text-sm md:text-base leading-relaxed text-white/60">
                        <span className="mt-2 block h-1.5 w-1.5 rotate-45 bg-brand-orange shrink-0" />
                        {l}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28 bg-brand-coal border-y border-white/[0.06]">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid gap-14 lg:grid-cols-12 items-center">
            <div className="lg:col-span-6">
              <Eyebrow>Allestimento wedding</Eyebrow>
              <MaskLines
                lines={["ENERGICO DI NOTTE.", "RAFFINATO PER IL SÌ."]}
                when="view"
                lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2rem,4.5vw,3.8rem)]"
                className="mt-6"
              />
              <Reveal delay={0.15}>
                <p className="mt-8 text-base md:text-lg leading-relaxed text-white/65 max-w-xl">
                  Rivestimenti effetto legno, luci ambrate, ombrellone avorio, fiori e tavoli
                  cocktail: lo stesso brand, raffinato per il vostro sì.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.1}>
                <ParallaxImage
                  src="/banco-wedding.jpg"
                  alt="Il banco Sprizziamoci con rivestimento effetto legno, luci ambrate e ombrellone avorio"
                  speed={0.12}
                  className="relative rounded-3xl h-[380px] md:h-[520px]"
                />
              </Reveal>
            </div>
          </div>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ALLESTIMENTI_VARIANTS.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.07}>
                <div className="group overflow-hidden rounded-2xl border border-white/[0.07] bg-brand-surface/30 h-full">
                  <div className="overflow-hidden">
                    <img
                      src={v.image}
                      alt={`Allestimento ${v.name}`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    />
                  </div>
                  <div className="p-6">
                    <span className="font-display italic text-brand-orange text-lg">{v.n}</span>
                    <h4 className="mt-1 font-display text-xl font-semibold uppercase tracking-wide">
                      {v.name}
                    </h4>
                    <p className="mt-2 text-sm text-white/55">{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <a
              href={SUPPLIER.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="allestimenti-supplier-card"
              className="group flex flex-col md:flex-row md:items-center justify-between gap-6 rounded-3xl border border-white/[0.08] bg-brand-surface/30 p-8 md:p-10 transition-colors duration-300 hover:border-brand-orange/40"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <span className="flex items-center rounded-2xl bg-brand-ivory px-6 py-4 shrink-0">
                  <img src="/pasquini.png" alt="Pasquini SRL" className="h-14 md:h-16 w-auto" />
                </span>
                <div>
                  <p className="eyebrow mb-2">{SUPPLIER.tag}</p>
                  <p className="font-display text-2xl md:text-3xl font-semibold">{SUPPLIER.name}</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/55 max-w-2xl">
                    {SUPPLIER.desc}
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-orange shrink-0 group-hover:underline underline-offset-4">
                Visita pasquinisrl.it
              </span>
            </a>
            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 mr-2">
                I marchi in spina
              </span>
              {SUPPLIER.brands.map((b) => (
                <span
                  key={b}
                  data-testid={`supplier-brand-${b.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs text-white/65"
                >
                  {b}
                </span>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-white/35 italic">{SUPPLIER.brandsNote}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 rounded-3xl border border-brand-orange/30 bg-brand-orange/[0.05] p-10 md:p-14">
              <div>
                <p className="eyebrow mb-3">Servizio in autonomia</p>
                <p className="font-display text-2xl md:text-4xl font-semibold leading-tight max-w-2xl">
                  I tuoi ospiti si servono da soli. Spina, ghiaccio e bicchieri sempre pronti.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 shrink-0">
                <CTAButton to="/contatti" testId="allestimenti-cta">
                  Richiedi un preventivo
                </CTAButton>
                <a
                  href="/scheda-tecnica-sprizziamoci.pdf"
                  download="Scheda-tecnica-Sprizziamoci.pdf"
                  data-testid="download-scheda"
                  className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:border-brand-orange hover:text-brand-orange transition-colors duration-300"
                >
                  <FileDown size={15} /> Scheda tecnica (PDF)
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </article>
  );
}
